import type React from 'react';
import classNames from 'classnames';

export interface SegmentedTabOption<TValue extends string> {
  value: TValue;
  label: string;
  disabled?: boolean;
}

export interface SegmentedTabProps<TValue extends string>
  extends Omit<React.FieldsetHTMLAttributes<HTMLFieldSetElement>, 'onChange'> {
  label: string;
  options: readonly SegmentedTabOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  ref?: React.Ref<HTMLFieldSetElement>;
}

const SegmentedTab = <TValue extends string>({
  label,
  options,
  value,
  onChange,
  className,
  ref,
  ...fieldsetProps
}: SegmentedTabProps<TValue>) => (
  <fieldset
    {...fieldsetProps}
    ref={ref}
    role="group"
    aria-label={label}
    className={classNames(
      'flex rounded-md border border-navy-200 overflow-hidden',
      className
    )}
  >
    <legend className="sr-only">{label}</legend>
    {options.map((option) => {
      const isActive = option.value === value;

      return (
        <button
          key={option.value}
          type="button"
          className={classNames(
            'px-2.5 py-1.5 text-xs font-semibold border-r border-navy-200 last:border-r-0 focus-visible:ring-2 ring-offset-2 ring-navy-800',
            {
              'bg-navy-100 text-navy-800': isActive,
              'bg-white text-navy-600': !isActive,
              'hover:bg-gray-50': !isActive && !option.disabled,
            }
          )}
          onClick={() => onChange(option.value)}
          aria-pressed={isActive}
          disabled={option.disabled}
        >
          {option.label}
        </button>
      );
    })}
  </fieldset>
);

export default SegmentedTab;
